/**
 * Generate a multi-segment SVG arc path for curved text.
 *
 * Uses multiple sub-arc segments (each ≤ 90°) instead of a single arc command
 * to avoid Fabric.js character-placement bugs at path seams — the same fix that
 * makes the circle preset work reliably.
 *
 * All curve presets use `pathSide: 'left'`. Arch-up produces a hill shape
 * (peaks upward) and arch-down produces a valley shape (dips downward).
 * Text follows the curve of the path in both cases.
 *
 * @param width     — chord length (text bounding width)
 * @param amount    — curve intensity 1..100
 * @param direction — 'up' or 'down'
 */
export const generateArcPath = (
  width: number,
  amount: number,
  direction: 'up' | 'down' = 'up'
): string => {
  if (amount === 0) return '';

  const absAmount = Math.abs(amount);
  // Map 1..100 → radius: 100 = tight (≈ width/2), 1 = gentle (≈ width * 5)
  const radius = width * (5 - (absAmount / 100) * 4.5);
  const halfW = width / 2;

  const sagitta =
    radius > halfW
      ? radius - Math.sqrt(radius * radius - halfW * halfW)
      : radius;

  // Half-angle subtended by the chord on the circle
  const halfAngle = Math.asin(Math.min(halfW / radius, 1));

  // Parameterization: point(θ) = (cx + R·sin(θ), cy − R·cos(θ))
  // θ=0 → top of circle (min y), θ=π → bottom (max y)
  const cx = halfW;
  let cy: number;
  let startAngle: number;
  let angleDelta: number; // total angle traversed (positive = increasing θ, negative = decreasing)
  let sweep: number;

  if (direction === 'up') {
    // Hill shape: center below chord, arc passes through top of circle
    // Path: (0, sagitta) → peak at (halfW, 0) → (width, sagitta)
    cy = radius;
    startAngle = -halfAngle;
    angleDelta = 2 * halfAngle;
    sweep = 1;
  } else {
    // Valley shape: center above chord, arc passes through bottom of circle
    // Path: (0, 0) → trough at (halfW, sagitta) → (width, 0)
    cy = sagitta - radius;
    startAngle = Math.PI + halfAngle;
    angleDelta = -2 * halfAngle;
    sweep = 0;
  }

  // Split into sub-segments of at most 90° each
  const segments = Math.max(2, Math.ceil(Math.abs(angleDelta) / (Math.PI / 2)));
  const step = angleDelta / segments;

  const pointOnArc = (angle: number) => ({
    x: cx + radius * Math.sin(angle),
    y: cy - radius * Math.cos(angle),
  });

  const start = pointOnArc(startAngle);
  const parts: string[] = [`M ${start.x} ${start.y}`];

  for (let i = 1; i <= segments; i++) {
    const pt = pointOnArc(startAngle + step * i);
    parts.push(`A ${radius} ${radius} 0 0 ${sweep} ${pt.x} ${pt.y}`);
  }

  return parts.join(' ');
};

/**
 * Generate an SVG circle path for text wrapping around a full circle.
 *
 * @param radius — circle radius
 */
export const generateCirclePath = (radius: number): string => {
  // Use four quarter-arc segments instead of two halves.
  // Two-arc paths have a degenerate seam that causes Fabric.js to
  // stop placing characters before completing the visual circle.
  const r = radius;
  return [
    `M ${r} 0`,                                  // top-center
    `A ${r} ${r} 0 0 1 ${r * 2} ${r}`,           // quarter: top → right
    `A ${r} ${r} 0 0 1 ${r} ${r * 2}`,           // quarter: right → bottom
    `A ${r} ${r} 0 0 1 0 ${r}`,                   // quarter: bottom → left
    `A ${r} ${r} 0 0 1 ${r} 0`,                   // quarter: left → top
  ].join(' ');
};

export type CurvePreset = 'none' | 'arch-up' | 'arch-down' | 'circle';

/**
 * Compute the exact visual bounds (width × height) that text-on-path will occupy,
 * accounting for glyph height extending perpendicular to the path.
 */
export const getCurveVisualBounds = (
  preset: CurvePreset,
  textWidth: number,
  fontSize: number,
  curveAmount: number,
  circleRadius?: number,
): { width: number; height: number } => {
  switch (preset) {
    case 'arch-up':
    case 'arch-down': {
      const absAmount = Math.abs(curveAmount) || 50;
      const radius = textWidth * (5 - (absAmount / 100) * 4.5);
      const halfW = textWidth / 2;
      const sagitta = radius > halfW
        ? radius - Math.sqrt(radius * radius - halfW * halfW)
        : radius;
      return { width: textWidth + fontSize, height: sagitta + fontSize };
    }
    case 'circle': {
      const r = circleRadius ?? textWidth / 2;
      // Characters render inside the circle (pathSide: left), extending inward by fontSize
      const diameter = r * 2;
      return { width: diameter + fontSize, height: diameter + fontSize };
    }
    default:
      return { width: textWidth, height: fontSize };
  }
};

/**
 * Compute the total arc-length of the SVG path for a given preset.
 */
export const getCurvePathLength = (
  preset: CurvePreset,
  width: number,
  curveAmount: number,
  circleRadius?: number,
): number => {
  switch (preset) {
    case 'circle': {
      const r = circleRadius ?? width / 2;
      return 2 * Math.PI * r;
    }
    case 'arch-up':
    case 'arch-down': {
      const absAmount = Math.abs(curveAmount) || 50;
      const radius = width * (5 - (absAmount / 100) * 4.5);
      const halfW = width / 2;
      const halfAngle = Math.asin(Math.min(halfW / radius, 1));
      return radius * 2 * halfAngle;
    }
    default:
      return width;
  }
};

/**
 * Compute the pathStartOffset that centers text on the curve path.
 * Uses canvas 2d measureText() for accurate text width measurement.
 */
export const getAutoPathStartOffset = (
  preset: CurvePreset,
  text: string,
  fontSize: number,
  fontFamily: string,
  fontWeight: number | string,
  fontStyle: string,
  charSpacing: number,
  pathWidth: number,
  curveAmount: number,
  circleRadius?: number,
): number => {
  if (preset === 'none') return 0;

  const pathLength = getCurvePathLength(preset, pathWidth, curveAmount, circleRadius);

  // Measure text advance using canvas 2d
  const ctx = document.createElement('canvas').getContext('2d');
  if (!ctx) return 0;
  ctx.font = `${fontStyle} ${fontWeight} ${fontSize}px ${fontFamily}`;
  const measuredWidth = ctx.measureText(text).width;

  // charSpacing is in 1/1000 em in Fabric.js
  const spacingPx = (charSpacing / 1000) * fontSize;
  const totalAdvance = measuredWidth + spacingPx * Math.max(0, text.length - 1);

  if (preset === 'circle') {
    // Circle: center text at the top (offset 0) by straddling the start/end point
    return Math.max(0, pathLength - totalAdvance / 2);
  }

  return Math.max(0, (pathLength - totalAdvance) / 2);
};

/**
 * Build the SVG path data string for a given preset and parameters.
 */
export const buildCurvePath = (
  preset: CurvePreset,
  width: number,
  curveAmount: number,
  circleRadius?: number,
): string | undefined => {
  switch (preset) {
    case 'none':
      return undefined;
    case 'arch-up':
      return generateArcPath(width, Math.abs(curveAmount) || 50, 'up');
    case 'arch-down':
      return generateArcPath(width, Math.abs(curveAmount) || 50, 'down');
    case 'circle':
      return generateCirclePath(circleRadius ?? width / 2);
    default:
      return undefined;
  }
};
